/**
 * 売上分配計算ユーティリティ（顧客要望③④⑤）
 *
 * カテゴリ別に「原価控除フラグ」と「利益控除率」を持ち、
 * 商品単位でインセンティブベース（分配対象額）を計算する。
 *
 * デフォルト挙動:
 *  - シャンパンカテゴリ: deductCost=true, profitDeductionRate=0.10
 *    → 小計 = (店売価 - 原価) × (1 - 0.10)
 *  - 他ドリンクカテゴリ: deductCost=false, profitDeductionRate=0.10
 *    → 小計 = 店売価 × (1 - 0.10)
 *  - 未設定カテゴリ: deductCost=false, profitDeductionRate=0
 *    → 小計 = 店売価（既存挙動）
 */

export interface CategoryDistributionConfig {
  deductCost?: boolean;
  profitDeductionRate?: number;
}

export interface OrderItemForDistribution {
  price?: number;
  quantity?: number;
  costInfo?: { cost?: number; baseBackRate?: number } | null;
  categoryId?: string;
  itemStatus?: string;
  transferToStaffId?: string;
  transferToStaffName?: string;
  transferReason?: string;
  // 商品単位の販売スタッフ（複数）+ 各々の取り分
  salesStaff?: { staffId: string; staffName?: string; shareRate: number }[];
}

/**
 * スタッフ個別オーバーライド (顧客要望: スタッフ別バック率)
 * staff.salesProfitDeductionRate が設定されていれば、カテゴリ既定 rate より優先される。
 */
export interface StaffDistributionConfig {
  salesProfitDeductionRate?: number;
}

export function buildStaffDistributionMap(
  staffList: { id: string; salesProfitDeductionRate?: number }[]
): Map<string, StaffDistributionConfig> {
  const m = new Map<string, StaffDistributionConfig>();
  for (const s of staffList) {
    if (s.salesProfitDeductionRate != null) {
      m.set(s.id, { salesProfitDeductionRate: s.salesProfitDeductionRate });
    }
  }
  return m;
}

/**
 * 1商品 1スタッフ分の分配対象額（取り分小計）を計算
 *
 * 計算式:
 *   portionPrice  = price × quantity × shareRate
 *   portionCost   = (deductCost ? cost × quantity × shareRate : 0)
 *   effectiveRate = staff.salesProfitDeductionRate ?? category.profitDeductionRate ?? 0
 *   portionBase   = (portionPrice - portionCost) × (1 - effectiveRate)
 */
export function calculateStaffPortionBase(
  item: OrderItemForDistribution,
  staffId: string,
  shareRate: number,
  categoryMap: Map<string, CategoryDistributionConfig>,
  staffMap: Map<string, StaffDistributionConfig>
): number {
  const qty = item.quantity ?? 1;
  const price = (item.price ?? 0) * qty * shareRate;
  if (price <= 0) return 0;
  const catCfg = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
  const deductCost = catCfg?.deductCost ?? false;
  const cost = deductCost ? (item.costInfo?.cost ?? 0) * qty * shareRate : 0;
  const staffCfg = staffMap.get(staffId);
  const effectiveRate =
    staffCfg?.salesProfitDeductionRate ?? catCfg?.profitDeductionRate ?? 0;
  const base = (price - cost) * (1 - effectiveRate);
  return Math.max(0, base);
}

/**
 * カテゴリIDから分配設定を取得（マップで O(1) 引き）
 */
export function buildCategoryDistributionMap(
  categories: { id: string; salesDistribution?: CategoryDistributionConfig }[]
): Map<string, CategoryDistributionConfig> {
  const map = new Map<string, CategoryDistributionConfig>();
  for (const cat of categories) {
    if (cat.salesDistribution) {
      map.set(cat.id, cat.salesDistribution);
    }
  }
  return map;
}

/**
 * 1商品の分配対象額（小計）を計算
 *
 * @returns 分配対象となる小計金額（端数切り捨てなし、整数切り捨ては呼び出し側で）
 */
export function calculateItemDistributionBase(
  item: OrderItemForDistribution,
  categoryMap: Map<string, CategoryDistributionConfig>
): number {
  const qty = item.quantity ?? 1;
  const price = (item.price ?? 0) * qty;
  if (price <= 0) return 0;

  const config = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
  const deductCost = config?.deductCost ?? false;
  const profitRate = config?.profitDeductionRate ?? 0;

  const cost = deductCost ? (item.costInfo?.cost ?? 0) * qty : 0;
  const afterCost = price - cost;
  const afterProfit = afterCost * (1 - profitRate);
  return Math.max(0, afterProfit);
}

/**
 * 注文全体のインセンティブベース集計（振替考慮）
 *
 * 戻り値:
 *  - generalBase: 振替なしのアイテム合計（通常の selectedSalesStaff で分配）
 *  - transfers: 振替先スタッフID別の小計マップ
 *  - totalCostDeduction: 原価控除合計（参考表示用）
 */
export interface DistributionSummary {
  generalBase: number;
  transfers: Map<string, { staffName: string; amount: number; reason?: string }>;
  /** スタッフID別の直接帰属額（item.salesStaff から） */
  perStaffBase: Map<string, { staffName: string; amount: number }>;
  totalCostDeduction: number;
}

/**
 * 注文全体のインセンティブベース集計
 *
 * staffMap が渡された場合、item.salesStaff[] 単位で per-staff portion 計算（推奨）
 * staffMap=undefined の場合、後方互換でアイテム合計を generalBase に集約
 */
export function summarizeDistribution(
  items: OrderItemForDistribution[],
  categoryMap: Map<string, CategoryDistributionConfig>,
  staffMap?: Map<string, StaffDistributionConfig>
): DistributionSummary {
  const transfers = new Map<string, { staffName: string; amount: number; reason?: string }>();
  const perStaffBase = new Map<string, { staffName: string; amount: number }>();
  let generalBase = 0;
  let totalCostDeduction = 0;

  for (const item of items) {
    if (item.itemStatus === 'cancelled') continue;

    const config = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
    if (config?.deductCost) {
      const qty = item.quantity ?? 1;
      totalCostDeduction += (item.costInfo?.cost ?? 0) * qty;
    }

    // 振替指定があれば全額（カテゴリ rate のみ適用、スタッフ override は対象外）
    if (item.transferToStaffId) {
      const base = calculateItemDistributionBase(item, categoryMap);
      if (base <= 0) continue;
      const existing = transfers.get(item.transferToStaffId);
      transfers.set(item.transferToStaffId, {
        staffName: item.transferToStaffName ?? existing?.staffName ?? '',
        amount: (existing?.amount ?? 0) + base,
        reason: item.transferReason ?? existing?.reason,
      });
      continue;
    }

    // 商品単位の salesStaff[] が存在し、staffMap も与えられていれば per-portion 計算
    if (staffMap && item.salesStaff && item.salesStaff.length > 0) {
      for (const s of item.salesStaff) {
        const portionBase = calculateStaffPortionBase(
          item,
          s.staffId,
          s.shareRate,
          categoryMap,
          staffMap
        );
        if (portionBase <= 0) continue;
        const existing = perStaffBase.get(s.staffId);
        perStaffBase.set(s.staffId, {
          staffName: s.staffName ?? existing?.staffName ?? '',
          amount: (existing?.amount ?? 0) + portionBase,
        });
      }
    } else {
      // staffMap なし or salesStaff[] なし → カテゴリ rate のみで一括計上
      const base = calculateItemDistributionBase(item, categoryMap);
      if (base > 0) generalBase += base;
    }
  }

  return { generalBase, transfers, perStaffBase, totalCostDeduction };
}
